/**
 * Local-first persistence with IndexedDB (via idb). Projects and settings live on
 * the device so the free product is useful with no account and works offline.
 */
import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type { DisplaySettings, Project } from '@goanna-im/shared';

interface PresenterDB extends DBSchema {
  projects: { key: string; value: Project };
  kv: { key: string; value: unknown };
}

let dbPromise: Promise<IDBPDatabase<PresenterDB>> | null = null;

function db(): Promise<IDBPDatabase<PresenterDB>> {
  if (!dbPromise) {
    dbPromise = openDB<PresenterDB>('goanna-im-presenter', 1, {
      upgrade(database) {
        if (!database.objectStoreNames.contains('projects')) {
          database.createObjectStore('projects', { keyPath: 'id' });
        }
        if (!database.objectStoreNames.contains('kv')) {
          database.createObjectStore('kv');
        }
      },
    });
  }
  return dbPromise;
}

export async function saveProject(project: Project): Promise<void> {
  (await db()).put('projects', project);
}

export async function listProjects(): Promise<Project[]> {
  const all = await (await db()).getAll('projects');
  return all.sort((a, b) => b.modifiedAt - a.modifiedAt);
}

export async function getProject(id: string): Promise<Project | undefined> {
  return (await db()).get('projects', id);
}

export async function deleteProject(id: string): Promise<void> {
  (await db()).delete('projects', id);
}

export async function saveSettings(settings: DisplaySettings): Promise<void> {
  (await db()).put('kv', settings, 'display-settings');
}

export async function loadSettings(): Promise<DisplaySettings | undefined> {
  return (await db()).get('kv', 'display-settings') as Promise<DisplaySettings | undefined>;
}

/** Wipe every local project and setting — the "Delete local data" action. */
export async function deleteAllLocalData(): Promise<void> {
  const database = await db();
  await database.clear('projects');
  await database.clear('kv');
}
